import type React from 'react';
import { useCallback, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { LoadingPage } from '../components/LoadingPage';
import { MeetingErrorPage } from '../components/MeetingErrorPage';
import { MobileAppRedirectPage } from '../components/MobileAppRedirectPage';
import { ExpiredSessionView, GuestJoinMeetingFlow } from '../components/SignInRequired';
import { useGuestSession } from '../contexts/GuestSessionContext';
import { useMeetingLink } from '../hooks/useMeetingLink';
import { useMobileAppRedirect } from '../hooks/useMobileAppRedirect';
import { useUser } from '../hooks/useUserData';
import { authStorageService } from '../services/authStorageService';
import { meetingSessionService } from '../services/meetingSessionService';

// NOTE: Global minimum meeting ID - will not attempt to join if less than 8 chars
// NOTE: This cannot be the same as Personal meeting minimum ID length since different platforms might
// NOTE: have different restrictions on meeting ID generation. The minimum we accept is 8 chars.
const ABSOLUTE_MIN_MEETING_ID_LEN = 8;

const MeetingLayout: React.FC = () => {
  const { isAuthenticated, idToken, login, isLoading } = useUser();
  const { meetingId } = useMeetingLink();
  const { guestSession, startGuestSession, clearGuestSession } = useGuestSession();
  const { promptPlatform, joinInBrowser } = useMobileAppRedirect(meetingId);
  const navigate = useNavigate();

  const isUnauthenticated = !(isAuthenticated && idToken);
  const isValidMeeting = meetingId && meetingId.length >= ABSOLUTE_MIN_MEETING_ID_LEN;
  // Keep a live meeting mounted through a cross-tab sign-out (which clears
  // idToken); the new session is applied on exit, when the service reloads.
  const keepActiveMeeting = meetingSessionService.isMeetingActive();

  useEffect(() => {
    return () => clearGuestSession();
  }, [clearGuestSession]);

  useEffect(() => {
    if (guestSession && guestSession.meetingId !== meetingId) {
      clearGuestSession();
    }
  }, [meetingId, guestSession, clearGuestSession]);

  const handleGuestJoin = useCallback(
    (name: string) => {
      const id = meetingId ?? '';
      startGuestSession({ meetingId: id, name });
      navigate(`/${id}`);
    },
    [meetingId, startGuestSession, navigate],
  );

  if (!isValidMeeting) {
    return (
      <MeetingErrorPage
        meetingId={meetingId || ''}
        errorDetail={'Meeting ID is not valid'}
        onBackToHome={() => {
          navigate('/');
        }}
      />
    );
  }

  // Ahead of the auth/guest fork, so the app gets its chance before the user is
  // asked to sign in or type a guest name they would only need in the browser.
  if (promptPlatform) {
    return (
      <MobileAppRedirectPage
        meetingId={meetingId ?? ''}
        platform={promptPlatform}
        onJoinInBrowser={joinInBrowser}
      />
    );
  }

  // SSO restore can take a second after the meeting route renders. Do not fork
  // to guest-join / expired-session (or mount Jitsi) until auth has settled.
  if (isLoading && !keepActiveMeeting) {
    return <LoadingPage room={meetingId || ''} />;
  }

  if (isUnauthenticated && authStorageService.hasLoggedInBefore() && !keepActiveMeeting) {
    return (
      <ExpiredSessionView
        meetingId={meetingId ?? ''}
        onSignIn={() => login()}
        onBack={() => navigate('/')}
      />
    );
  }

  if (isUnauthenticated && !guestSession && !keepActiveMeeting) {
    return (
      <GuestJoinMeetingFlow
        meetingId={meetingId ?? ''}
        onSignIn={() => login()}
        onGuestJoin={handleGuestJoin}
      />
    );
  }

  return <Outlet />;
};

export default MeetingLayout;
